import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHelmet from '@/components/feature/SEOHelmet';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { aboutInfo } from '@/mocks/about';

function ContactForm() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '', website_alt: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honey = new FormData(form).get('website_alt') as string;
    if (honey && honey.trim()) {
      setStatus('success');
      setStatusMessage('感谢您的留言，我们会尽快回复！');
      return;
    }
    setStatus('loading');
    try {
      const fd = new FormData(form);
      fd.delete('website_alt');
      const body = new URLSearchParams();
      fd.forEach((value, key) => { body.append(key, value as string); });
      const res = await fetch('https://readdy.ai/api/form/d9u4ovn8l97n3eas3hcg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const text = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }
      if (res.ok && (parsed as { code?: string }).code === 'OK') {
        setStatus('success');
        setStatusMessage('感谢您的留言，我们会尽快回复！');
        setFormData({ name: '', email: '', message: '', website_alt: '' });
      } else {
        const msg = ((parsed as { meta?: { message?: string } }).meta?.message)
          || ((parsed as { message?: string }).message)
          || '提交失败，请稍后再试';
        setStatus('error');
        setStatusMessage(msg);
      }
    } catch {
      setStatus('error');
      setStatusMessage('网络错误，请检查连接后重试');
    }
  };

  return (
    <div>
      <h3 className="text-base font-bold text-foreground-900 mb-4">给我们留言</h3>
      <form onSubmit={handleSubmit} className="flex flex-col gap-3" data-readdy-form>
        <input
          type="text"
          name="name"
          placeholder="你的昵称"
          value={formData.name}
          onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
          required
          className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors duration-150"
        />
        <input
          type="email"
          name="email"
          placeholder="你的邮箱"
          value={formData.email}
          onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
          required
          className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors duration-150"
        />
        <textarea
          name="message"
          placeholder="你想说的话..."
          value={formData.message}
          onChange={e => setFormData(p => ({ ...p, message: e.target.value }))}
          required
          maxLength={500}
          rows={4}
          className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors duration-150 resize-none"
        />
        <input
          type="text"
          name="website_alt"
          tabIndex={-1}
          autoComplete="off"
          readOnly
          aria-hidden="true"
          className="honeypot-field"
        />
        <button
          type="submit"
          disabled={status === 'loading'}
          className="px-6 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors duration-150 cursor-pointer whitespace-nowrap disabled:opacity-60"
        >
          {status === 'loading' ? '发送中...' : '发送留言'}
        </button>
        {status !== 'idle' && (
          <p className={`text-xs ${status === 'success' ? 'text-secondary-700' : 'text-primary-600'}`}>
            {statusMessage}
          </p>
        )}
      </form>
    </div>
  );
}

export default function AboutPage() {
  return (
    <>
      <SEOHelmet
        title="关于我们 - 宅宅资源库 | 二次元资源聚合平台"
        description="宅宅资源库成立于2024年，由一群热爱二次元文化的资深宅友共同创建。了解我们的品牌故事、发展历程与联系方式。"
        keywords="宅宅资源库,关于我们,二次元平台,资源聚合,联系我们"
        canonicalUrl="https://doujincenter.github.io/about"
      />
      <SchemaOrg type="Organization" data={{
        name: aboutInfo.brandName,
        url: 'https://doujincenter.github.io',
        description: aboutInfo.brandIntro,
        email: aboutInfo.contact.email,
        address: { '@type': 'PostalAddress', addressLocality: '上海', addressCountry: 'CN' },
      }} />
      <SchemaOrg type="WebPage" data={{
        name: `关于 ${aboutInfo.brandName}`,
        url: 'https://doujincenter.github.io/about',
        isPartOf: { '@type': 'WebSite', name: aboutInfo.brandName, url: 'https://doujincenter.github.io' },
      }} />

      <Navbar />
      <div className="pt-16">
        <Breadcrumb items={[
          { label: '首页', to: '/' },
          { label: '关于我们' },
        ]} />
      </div>

      <main>
        {/* Brand Intro */}
        <section className="w-full px-4 md:px-6 py-14 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-primary-100">
                <i className="ri-home-smile-2-line text-primary-600 text-3xl"></i>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4">关于 {aboutInfo.brandName}</h1>
              <p className="text-sm text-foreground-600 leading-relaxed max-w-2xl mx-auto">
                {aboutInfo.brandIntro}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 mb-4">
                  <i className="ri-focus-2-line text-primary-600 text-xl"></i>
                </div>
                <h3 className="text-base font-bold text-foreground-900 mb-2">我们的使命</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{aboutInfo.mission}</p>
              </div>
              <div className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 mb-4">
                  <i className="ri-eye-line text-accent-600 text-xl"></i>
                </div>
                <h3 className="text-base font-bold text-foreground-900 mb-2">我们的愿景</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{aboutInfo.vision}</p>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {aboutInfo.stats.map(stat => (
                <div key={stat.label} className="text-center p-5 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="text-2xl md:text-3xl font-bold text-primary-600">{stat.value}</div>
                  <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="w-full px-4 md:px-6 py-14 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">发展历程</h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-300 md:-translate-x-px"></div>
              <div className="flex flex-col gap-8">
                {aboutInfo.milestones.map((milestone, idx) => (
                  <div key={idx} className={`relative flex items-start gap-6 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className={`flex-1 ${idx % 2 === 0 ? 'md:text-right' : 'md:text-left'} pl-12 md:pl-0`}>
                      <div className="bg-background-50 rounded-xl p-4 border border-background-200/70 inline-block">
                        <span className="text-xs font-bold text-primary-600">{milestone.year}</span>
                        <p className="text-sm text-foreground-700 mt-1">{milestone.event}</p>
                      </div>
                    </div>
                    <div className="absolute left-2.5 md:left-1/2 md:-translate-x-1/2 w-3 h-3 rounded-full bg-primary-500 border-2 border-background-100 mt-1.5"></div>
                    <div className="flex-1 hidden md:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="w-full px-4 md:px-6 py-14 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">宅友口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {aboutInfo.testimonials.map((t, idx) => (
                <div key={idx} className="bg-background-100 rounded-xl p-5 border border-background-200/70">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                      <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="w-full px-4 md:px-6 py-14 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">联系我们</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-base font-bold text-foreground-900 mb-4">联系方式</h3>
                <div className="flex flex-col gap-3">
                  {[
                    { icon: 'ri-mail-line', label: '邮箱', value: aboutInfo.contact.email },
                    { icon: 'ri-qq-line', label: 'QQ群', value: aboutInfo.contact.qq },
                    { icon: 'ri-wechat-line', label: '微信', value: aboutInfo.contact.wechat },
                    { icon: 'ri-telegram-line', label: 'Telegram', value: aboutInfo.contact.telegram },
                  ].map(item => (
                    <div key={item.label} className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                        <i className={`${item.icon} text-primary-600`}></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">{item.label}</p>
                        <p className="text-sm font-medium text-foreground-800">{item.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-5">
                  <p className="text-xs text-foreground-500 mb-1">地址</p>
                  <p className="text-sm text-foreground-700">{aboutInfo.contact.address}</p>
                </div>
              </div>

              <ContactForm />
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}